import _ from 'lodash';
import { useContext, useEffect, useState } from 'react';

import { StoreContext } from '../../StoreContext';
import { updateSelectedView } from '../../actions';
import { useCurrentView, viewsAreEqual } from './view';

/**
 * The saved view the user is currently looking at.
 *
 * This is *derived* from the current view rather than remembered: as soon as
 * the user changes a filter, concept list, etc. the current view no longer
 * matches any saved one, so the selection clears itself. Several saved views
 * can be identical, so the one the user explicitly loaded last wins.
 *
 * Also keeps the store's `selectedView` in sync — the filter panel uses it to
 * decide which sections to expand.
 *
 * @returns {[object|undefined, function]} the matching saved view (if any) and
 *   a setter to record the view that was just loaded.
 */
export function useSelectedSharedView() {
  const { sharedViews } = useContext(StoreContext);
  const currentView = useCurrentView();
  const [lastLoadedName, setLastLoadedName] = useState('');

  const equalsCurrentView = sharedView =>
    sharedView != null &&
    viewsAreEqual(
      currentView.view,
      _.omit(sharedView, 'name', 'note', 'shared_view_id')
    );

  const lastLoaded = _.find(sharedViews, { name: lastLoadedName });
  const selectedView = equalsCurrentView(lastLoaded)
    ? lastLoaded
    : _.find(sharedViews, equalsCurrentView);

  const selectedViewId = selectedView?.shared_view_id;
  useEffect(() => {
    updateSelectedView(selectedView ?? null);
    // Only re-dispatch when the identity of the selected view changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedViewId]);

  return [selectedView, setLastLoadedName];
}
